import {
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  MarketingCampaignEntryStatus,
  MarketingCampaignRunType,
} from '@principle-theorem/principle-core/interfaces';
import {
  bufferedQuery$,
  DocumentReference,
  Firestore,
  isSameRef,
  limit,
  multiMergeMap,
  orderBy,
  Query,
  QueryConstraint,
  toQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { from, Observable } from 'rxjs';
import { scan } from 'rxjs/operators';
import { MarketingCampaign } from './campaign';
import { MarketingCampaignRun } from './campaign-run';

export class CampaignMaxEntryOverflowManager {
  private _campaignRunCache = new Map<string, WithRef<IMarketingCampaignRun>>();

  async moveToNewCampaignRun(
    fromCampaignRun: WithRef<IMarketingCampaignRun>,
    toCampaignRun: WithRef<IMarketingCampaignRun>,
    approveEntries: boolean = false,
    concurrency: number = 50
  ): Promise<void> {
    return this.enrolOverflowEntries(
      toCampaignRun,
      fromCampaignRun,
      false,
      approveEntries,
      concurrency
    );
  }

  async moveFromAutoCampaignRuns(
    toCampaignRun: WithRef<IMarketingCampaignRun>,
    approveEntries: boolean = false,
    concurrency: number = 50
  ): Promise<void> {
    return this.enrolOverflowEntries(
      toCampaignRun,
      undefined,
      true,
      approveEntries,
      concurrency
    );
  }

  async enrolOverflowEntries(
    campaignRun: WithRef<IMarketingCampaignRun>,
    onlyFromCampaignRun?: WithRef<IMarketingCampaignRun>,
    onlyFromAutoCampaignRuns: boolean = false,
    approveEntries: boolean = false,
    concurrency: number = 50
  ): Promise<void> {
    this._campaignRunCache.clear();

    if (onlyFromCampaignRun) {
      const fromCampaignRef =
        MarketingCampaignRun.campaignRef(onlyFromCampaignRun);
      const toCampaignRef = MarketingCampaignRun.campaignRef(campaignRun);
      if (!isSameRef(fromCampaignRef, toCampaignRef)) {
        // eslint-disable-next-line no-console
        console.warn(
          'Campaign run does not belong to the same campaign as the overflow entries'
        );
        return;
      }
    }

    const campaign = await Firestore.getDoc(
      MarketingCampaignRun.campaignRef(campaignRun)
    );

    if (approveEntries) {
      const remainingEntries =
        await MarketingCampaignRun.getAvailableEntryCount(campaignRun);
      const approvableEntries = Firestore.getDocs(
        this._approvableOverflowEntriesQuery(
          campaign,
          onlyFromCampaignRun,
          remainingEntries
        )
      );
      await this._processEntries(
        campaignRun,
        onlyFromAutoCampaignRuns,
        true,
        from(approvableEntries),
        concurrency
      );
    }

    const overflowEntries$ = bufferedQuery$(
      this._overflowEntriesQuery(campaign, onlyFromCampaignRun),
      concurrency,
      'ref'
    );
    await this._processEntries(
      campaignRun,
      onlyFromAutoCampaignRuns,
      false,
      overflowEntries$,
      concurrency
    );
  }

  private _approvableOverflowEntriesQuery(
    campaign: WithRef<IMarketingCampaign>,
    campaignRun?: WithRef<IMarketingCampaignRun>,
    limitEntries?: number
  ): Query<IMarketingCampaignEntry> {
    const contraints: QueryConstraint[] = [
      where('status', '==', MarketingCampaignEntryStatus.Preview),
      where('deleted', '==', false),
      where('hasDuplicateEmail', '==', false),
      where('hasDuplicateMobile', '==', false),
      orderBy('createdAt', 'asc'), // Enroll oldest entries first
    ];
    if (campaignRun) {
      contraints.push(where('campaignRunRef', '==', campaignRun.ref));
    }
    if (limitEntries) {
      contraints.push(limit(limitEntries));
    }
    return toQuery(
      MarketingCampaign.marketingCampaignEntryCol(campaign),
      ...contraints
    );
  }

  private _overflowEntriesQuery(
    campaign: WithRef<IMarketingCampaign>,
    campaignRun?: WithRef<IMarketingCampaignRun>
  ): Query<IMarketingCampaignEntry> {
    const contraints: QueryConstraint[] = [
      where('status', '==', MarketingCampaignEntryStatus.Preview),
      where('deleted', '==', false),
    ];

    if (campaignRun) {
      contraints.push(where('campaignRunRef', '==', campaignRun.ref));
    }
    return toQuery(
      MarketingCampaign.marketingCampaignEntryCol(campaign),
      ...contraints
    );
  }

  private async _processEntries(
    campaignRun: WithRef<IMarketingCampaignRun>,
    onlyFromAutoCampaignRuns: boolean,
    approveEntries: boolean,
    entries$: Observable<WithRef<IMarketingCampaignEntry>[]>,
    concurrency: number = 50
  ): Promise<void> {
    await entries$
      .pipe(
        multiMergeMap(concurrency, (entry) =>
          this._enrolOverflowEntry(
            campaignRun,
            onlyFromAutoCampaignRuns,
            approveEntries,
            entry
          )
        ),
        scan((acc) => acc + 1, 0)
      )
      .toPromise();
  }

  private async _enrolOverflowEntry(
    campaignRun: WithRef<IMarketingCampaignRun>,
    onlyFromAutoCampaignRuns: boolean,
    approveEntry: boolean,
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    if (isSameRef(entry.campaignRunRef, campaignRun.ref)) {
      return;
    }
    if (onlyFromAutoCampaignRuns) {
      const isFromAuto = await this._isFromAutoCampaignRun(entry);
      if (!isFromAuto) {
        return;
      }
    }
    const updates: Partial<IMarketingCampaignEntry> = {
      campaignRunRef: campaignRun.ref,
    };
    if (approveEntry) {
      updates.status = MarketingCampaignEntryStatus.Approved;
    }
    await Firestore.patchDoc(entry.ref, updates);
  }

  private async _isFromAutoCampaignRun(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<boolean> {
    const campaignRun = await this._loadCampaignRun(entry.campaignRunRef);
    return campaignRun.type === MarketingCampaignRunType.Automatic;
  }

  private async _loadCampaignRun(
    ref: DocumentReference<IMarketingCampaignRun>
  ): Promise<WithRef<IMarketingCampaignRun>> {
    const existing = this._campaignRunCache.get(ref.path);
    if (existing) {
      return existing;
    }
    const campaignRun = await Firestore.getDoc(ref);
    this._campaignRunCache.set(ref.path, campaignRun);
    return campaignRun;
  }
}
